"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useLayoutEffect } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { type RegisteredSidebarDefinition, type SidebarDefinition, type SidebarSnapshot, defineSidebar } from "@/types/sidebar-session";
import { useAskDecodaStore } from "@/stores/ask-decoda-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";

/** Root-owned subscription: conversations must not outlive the authenticated scope,
 * even when the Ask panel itself is not mounted. Install before environment sync. */
export function useAssistantScopeReset() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useLayoutEffect(_temp2, t0);
}

/** Route and conversation ownership belong to AI, not the generic registry. */
function _temp2() {
  return useRightSidebarStore.subscribe(_temp);
}
function _temp(state, previous) {
  if (state.environment.ownerScope !== previous.environment.ownerScope) {
    useAskDecodaStore.getState().clearAllConversations();
  }
}
export function useAssistantSidebarReconciliation() {
  const $ = _c(8);
  const path = usePathname();
  const threadId = useAskDecodaStore(_temp3);
  const question = useAskDecodaStore(_temp4);
  const fileCount = useAskDecodaStore(_temp5);
  const pageSignature = useRightSidebarStore(_temp7);
  let t0;
  if ($[0] !== path) {
    t0 = () => {
      useRightSidebarStore.getState().open(assistantSidebarDefinition, {
        command: AssistantSidebarCommand.RECONCILE,
        path
      });
    };
    $[0] = path;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== fileCount || $[3] !== pageSignature || $[4] !== path || $[5] !== question || $[6] !== threadId) {
    t1 = [path, threadId, question, fileCount, pageSignature];
    $[2] = fileCount;
    $[3] = pageSignature;
    $[4] = path;
    $[5] = question;
    $[6] = threadId;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  useEffect(t0, t1);
}
function _temp7(state_2) {
  return selectPageAssistantDefinitions(state_2).map(_temp6).join("|");
}
function _temp6(definition) {
  return `${definition.id}:${definition.order}:${Boolean(definition.replacesAskDecoda)}`;
}
function _temp5(state_1) {
  return state_1.initialFiles.length;
}
function _temp4(state_0) {
  return state_0.initialQuestion;
}
function _temp3(state) {
  return state.activeThreadId;
}
const AISidebarPanel = dynamic(() => import("@/components/ai-sidebar/ai-sidebar-panel").then(m => m.AISidebarPanel));
export enum AssistantSidebarGroup {
  ASSISTANT = "ASSISTANT",
}
export enum AssistantSidebarCommand {
  OPEN = "OPEN",
  RECONCILE = "RECONCILE",
  PAGE_MOUNT = "PAGE_MOUNT",
  CLOSE_GROUP = "CLOSE_GROUP",
  FULLPAGE = "FULLPAGE",
}
export interface AskDecodaSidebarHandoff {
  ownerPath: string;
  nextPath?: string;
}
export interface AssistantSidebarPayload {
  handoff?: AskDecodaSidebarHandoff;
}
export type PageAssistantDefinition = SidebarDefinition<void, AssistantSidebarPayload> & {
  replacesAskDecoda?: boolean;
};
export function isCurrentAskDecodaHandoff(handoff: AskDecodaSidebarHandoff | null | undefined, path: string, conversation: Pick<ReturnType<typeof useAskDecodaStore.getState>, "activeThreadId" | "initialQuestion" | "initialFiles">): boolean {
  return Boolean(handoff && (path === handoff.ownerPath || path === handoff.nextPath) && (conversation.activeThreadId || conversation.initialQuestion.trim() || conversation.initialFiles.length));
}
export const askDecodaSidebarDefinition: SidebarDefinition<void, AssistantSidebarPayload> = defineSidebar<void, AssistantSidebarPayload>({
  id: "ASK_DECODA",
  label: "Ask Decoda",
  order: 10000,
  group: AssistantSidebarGroup.ASSISTANT,
  isAvailable: env => env.capabilities.ASK_DECODA !== false,
  render: () => <AISidebarPanel />,
  strategy: {
    planOpen: context => ({
      upsert: [context.write(askDecodaSidebarDefinition, {})],
      select: "ASK_DECODA"
    }),
    planClose: () => ({
      effects: [() => useAskDecodaStore.getState().handleSidebarClosed()]
    })
  }
});
export function selectPageAssistantDefinitions(snapshot: SidebarSnapshot): PageAssistantDefinition[] {
  return Object.values(snapshot.registrations).map(({
    definition
  }) => definition).filter(definition => definition.group === AssistantSidebarGroup.ASSISTANT && definition.id !== askDecodaSidebarDefinition.id).filter(definition => definition.isAvailable?.(snapshot.environment) !== false).sort((a, b) => a.order - b.order) as unknown as PageAssistantDefinition[];
}
export function selectAssistantPanels(snapshot: SidebarSnapshot): RegisteredSidebarDefinition[] {
  return Object.values(snapshot.entries).map(entry => snapshot.registrations[entry.key]?.definition).filter((definition): definition is RegisteredSidebarDefinition => Boolean(definition?.group === AssistantSidebarGroup.ASSISTANT)).sort((a, b) => a.order - b.order);
}
export function createPageAssistantDefinition(options: {
  id: string;
  label: string;
  order: number;
  replacesAskDecoda?: boolean;
  render: () => ReactNode;
}): PageAssistantDefinition {
  const definition: PageAssistantDefinition = {
    ...options,
    order: 100 + options.order,
    group: AssistantSidebarGroup.ASSISTANT,
    isAvailable: environment => environment.capabilities.ASSISTANT !== false,
    strategy: {
      planOpen: context => ({
        upsert: [context.write(definition, {})],
        select: definition.id
      })
    }
  };
  return definition;
}
export interface AssistantSidebarInput {
  targetId?: string;
  nextPath?: string;
  command?: AssistantSidebarCommand;
  path?: string;
}

/** A virtual command: the launcher never owns a sidebar entry. */
export const assistantSidebarDefinition: SidebarDefinition<AssistantSidebarInput, never> = defineSidebar<AssistantSidebarInput, never>({
  id: "ASSISTANT_LAUNCHER",
  label: "Assistant",
  order: 100,
  strategy: {
    planOpen: (context, input) => {
      const {
        snapshot
      } = context;
      if (input.command === AssistantSidebarCommand.FULLPAGE) {
        return {
          remove: [askDecodaSidebarDefinition.id]
        };
      }
      if (input.command === AssistantSidebarCommand.CLOSE_GROUP) {
        return {
          // Registered assistants can own pending resume operations before
          // they have an entry. Dismiss the whole group, including those keys,
          // so late history cannot reopen a panel after the user toggles AI off.
          remove: [...new Set([askDecodaSidebarDefinition.id, ...Object.values(snapshot.registrations).map(({
            definition
          }) => definition).filter(definition => definition.group === AssistantSidebarGroup.ASSISTANT).map(definition => definition.id), ...selectAssistantPanels(snapshot).map(definition => definition.id)])],
          effects: context.read(askDecodaSidebarDefinition) ? [() => useAskDecodaStore.getState().handleSidebarClosed()] : []
        };
      }
      const path = input.path ?? snapshot.environment.path;
      const pages = selectPageAssistantDefinitions(snapshot);
      const replacement = pages.find(page => page.replacesAskDecoda);
      const ask = context.read(askDecodaSidebarDefinition);
      const conversation = useAskDecodaStore.getState();
      const validHandoff = isCurrentAskDecodaHandoff(ask?.payload.handoff, path, conversation);
      if (input.command === AssistantSidebarCommand.RECONCILE) {
        if (!ask) return {};
        if (validHandoff && ask.payload.handoff) {
          return path === ask.payload.handoff.nextPath ? {
            upsert: [context.write(askDecodaSidebarDefinition, {
              handoff: {
                ownerPath: path
              }
            })]
          } : {};
        }
        if (replacement) {
          // Metadata alone cannot reopen a missing (possibly dismissed) page.
          // PAGE_MOUNT owns first opening; an expired explicit override owns
          // restoring the contextual entry that the override had removed.
          if (!ask.payload.handoff && !context.read(replacement)) return {};
          return {
            remove: [askDecodaSidebarDefinition.id],
            ...(context.read(replacement) ? {} : {
              upsert: [context.write(replacement, {})]
            }),
            ...(snapshot.activeKey === askDecodaSidebarDefinition.id ? {
              select: replacement.id
            } : {})
          };
        }
        return ask.payload.handoff ? {
          upsert: [context.write(askDecodaSidebarDefinition, {})]
        } : {};
      }
      if (input.command === AssistantSidebarCommand.PAGE_MOUNT) {
        if (selectAssistantPanels(snapshot).length === 0) return {};
        const page = pages.find(definition => definition.id === input.targetId);
        if (!page || page.replacesAskDecoda && validHandoff) return {};
        return {
          upsert: [context.write(page, {})],
          ...(page.replacesAskDecoda ? {
            remove: [askDecodaSidebarDefinition.id],
            ...(snapshot.activeKey === askDecodaSidebarDefinition.id ? {
              select: page.id
            } : {})
          } : {})
        };
      }
      const requestedHandoff = input.targetId === askDecodaSidebarDefinition.id ? {
        ownerPath: path,
        nextPath: input.nextPath
      } : undefined;
      const handoff = isCurrentAskDecodaHandoff(requestedHandoff, path, conversation) ? requestedHandoff : undefined;
      const target = input.targetId === askDecodaSidebarDefinition.id ? replacement && !handoff ? replacement : askDecodaSidebarDefinition : pages.find(page => page.id === input.targetId) ?? replacement ?? pages[0] ?? askDecodaSidebarDefinition;
      if (target.isAvailable?.(snapshot.environment) === false) return {};
      if (target.id === askDecodaSidebarDefinition.id && /\/ask-decoda\/?$/.test(path) && !(handoff && input.nextPath)) return {};
      const definitions = [...pages.filter(page => !(handoff && page.id === replacement?.id)), ...((!replacement || handoff) && askDecodaSidebarDefinition.isAvailable?.(snapshot.environment) !== false ? [askDecodaSidebarDefinition] : [])];
      return {
        upsert: definitions.map(definition => context.write(definition, definition.id === askDecodaSidebarDefinition.id ? {
          handoff
        } : {})),
        remove: replacement ? [handoff ? replacement.id : askDecodaSidebarDefinition.id] : [],
        select: target.id
      };
    }
  }
});